import React, { useEffect, useState } from 'react';
import { Container, Row, Button } from 'reactstrap';
import { connect } from 'react-redux';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { useLocation, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon';
import TimetableIcon from 'mdi-react/TimetableIcon';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import MessageTextOutlineIcon from 'mdi-react/MessageTextOutlineIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import moment from 'moment';
import { toast } from 'react-toastify';
import renderField from '../../../shared/components/custom/Field';
import renderTextAreaField from '../../../shared/components/custom/TextArea';
import FormCard from '../../../shared/components/custom/FormCard';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import t, { partial } from '../../../util/translation/translation';
import Loading from '../../../shared/components/custom/Loading';
import Api from '../../../util/api';
import useModal from '../../../shared/hooks/useModal';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import validateGoalSponsorContact from './validateGoalSponsorContact';
import renderSelectField from '../../../shared/components/form/Select';
import PreviewMessages from '../../messages/components/PreviewsMessage';
import { isFutureTime } from '../../../util/date-and-time';

const GoalSponsorContactForm = ({
  subscriptionPlan,
  setScheduleMessageID,
  scheduleMessageID,
  scheduleMessagesForEvent,
  getScheduleMessagesByServiceId,
  startDate,
  setStartDate,
  initialize,
  messageStatus,
  formValues,
  handleSubmit,
  change,
  emailRecipientCount,
}) => {
  const isEmail = formValues?.type === 'email';
  const p = partial('EventsContactForm');
  const m = partial('MessagesForm');
  const g = partial('goalSponsor');
  const s = partial('shared');

  const { state: locationState } = useLocation();

  const [loading, setLoading] = useState(false);
  const [handleModal, visible, deleteId] = useModal();
  const navigationTypeOptions = [
    { value: 'plan', label: g('goalSponsor') },
    { value: 'leaderboard', label: g('leaderboard') },
  ];
  const receiverTypeOptions = [
    { value: 'all', label: s('all') },
    { value: 'participants', label: s('participants') },
  ];

  const initializeEdit = (message) => {
    setScheduleMessageID(message.id);
    const messageDate = message.date ? new Date(message.date) : null;
    setStartDate(messageDate);
    initialize({
      title:
        message?.payload?.placeholders?.TITLE ||
        message?.payload?.placeholders?.SUBJECT,
      message: message?.payload?.placeholders?.BODY,
      type: message?.payload?.channels[0]?.channel,
      scheduleTime: messageDate,
      timeToSend: 'SCHEDULED',
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const removeMessage = async (messageId) => {
    setLoading(true);
    try {
      await Api.goalSponsor.communication.deleteScheduledMessage(messageId);
      handleModal();
      getScheduleMessagesByServiceId();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err || 'messageDeletingERROR');
    }
  };

  const clearFields = () => {
    setScheduleMessageID(null);
    setStartDate('');
    initialize({
      title: '',
      message: '',
      type: 'email',
      scheduleTime: null,
      timeToSend: 'INSTANT',
    });
  };
  useEffect(() => {
    setScheduleMessageID(null);
  }, [subscriptionPlan, messageStatus]);

  useEffect(() => {
    if (locationState?.messageId && scheduleMessagesForEvent.length > 0) {
      const messageToShow = scheduleMessagesForEvent.find(
        (msg) => msg.id === locationState?.messageId
      );
      initializeEdit(messageToShow);
    }
  }, [scheduleMessagesForEvent]);

  const renderSubmitButtons = () => {
    let buttonName = '';
    if (scheduleMessageID) {
      buttonName = t('ScheduledMessagesPage.updateMessage');
    } else if (formValues?.timeToSend === 'SCHEDULED') {
      buttonName = t('ScheduledMessagesPage.scheduleMessage');
    } else {
      buttonName = p('sendMessage');
    }
    return (
      <div className="messages-form__footer">
        <div className="messages-form__secondary">
          <Link style={{ textDecoration: 'underline' }} to="/messages?tab=2">
            {t('MessagesForm.scheduledAndHistory')}
          </Link>
          {scheduleMessageID && (
            <Button
              type="button"
              size="sm"
              outline
              color="secondary"
              className="mb-0"
              onClick={clearFields}
            >
              {t('shared.clear')}
            </Button>
          )}
        </div>
        <Button type="submit" color="primary" className="mb-0">
          {buttonName}
        </Button>
      </div>
    );
  };
  const isSms = formValues?.type === 'sms';
  // SMS is switched off for goal sponsors; it's only listed when an edited
  // message already uses it, so its channel still shows
  const typeOptions = [
    {
      value: 'push',
      icon: BellRingOutlineIcon,
      label: 'Push',
      hint: m('pushMessageHint'),
    },
    {
      value: 'sms',
      icon: MessageTextOutlineIcon,
      label: 'SMS',
      hint: m('smsHint'),
    },
    {
      value: 'email',
      icon: EmailOutlineIcon,
      label: 'Email',
      hint: m('emailHint'),
    },
  ].filter(
    (option) => option.value !== 'sms' || option.value === formValues?.type
  );
  return (
    <form onSubmit={handleSubmit} className="form d-flex messages-form">
      <Loading loading={loading} />
      <Container className="padding-0">
        <DeleteModal
          visible={visible}
          handleModal={handleModal}
          type="message"
          modalAction={() => removeMessage(deleteId)}
        />
        <Row>
          <div className="col-12 col-xl-6">
            <Field
              name="type"
              component={ChoiceCards}
              ariaLabel={t('shared.type')}
              options={typeOptions}
            />

            <FormCard title={t('shared.content')} className="mb-3">
              <div className="form-grid">
                {/* An SMS has no title, so the field is hidden for SMS */}
                {!isSms && (
                  <div className="form__form-group form-grid__full">
                    <span className="form-header">{t('shared.title')}</span>
                    <Field
                      name="title"
                      placeholder={`${t('shared.title')}`}
                      component={renderField}
                      emoji
                    />
                  </div>
                )}
                <div className="form__form-group form-grid__full">
                  <span className="form-header">{t('shared.message')}</span>
                  <Field
                    name="message"
                    placeholder={p('messageContent')}
                    component={renderTextAreaField}
                    emoji
                  />
                </div>
              </div>
            </FormCard>

            {/* Emails always go to the plan's supporters */}
            {!isEmail && (
              <FormCard title={t('OfferForm.targetAudience')} className="mb-3">
                <div className="form-grid">
                  <div className="form__form-group">
                    <span className="form-header">{g('receiverType')}</span>
                    <Field
                      name="receiverType"
                      placeholder={g('selectReceiverType')}
                      options={receiverTypeOptions}
                      component={renderSelectField}
                    />
                  </div>
                </div>
              </FormCard>
            )}

            <FormCard title={t('OfferCard.other')} className="mb-3">
              <div className="form-grid">
                <div className="form__form-group">
                  <span className="form-header">{g('navigationType')}</span>
                  <Field
                    name="navigationType"
                    placeholder={g('selectNavigationType')}
                    options={navigationTypeOptions}
                    component={renderSelectField}
                  />
                </div>
              </div>
            </FormCard>

            <FormCard
              title={t('ScheduledMessagesPage.timeToSend')}
              className="mb-3"
            >
              <Field
                name="timeToSend"
                component={ChoiceCards}
                ariaLabel={t('ScheduledMessagesPage.timeToSend')}
                options={[
                  { value: 'INSTANT', label: t('shared.instant') },
                  { value: 'SCHEDULED', label: t('shared.scheduled') },
                ]}
              />
              {formValues?.timeToSend === 'SCHEDULED' && (
                <div className="form-grid">
                  <div className="form__form-group" style={{ zIndex: 100 }}>
                    <span className="form-header">
                      {t('ScheduledMessagesPage.scheduleTime')}
                    </span>
                    <div className="form__form-group-field">
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          change('scheduleTime', date);
                        }}
                        showTimeSelect
                        filterTime={isFutureTime}
                        timeFormat="p"
                        timeIntervals={15}
                        dateFormat="Pp"
                        minDate={moment().toDate()}
                        placeholderText="DD / MM / YYYY  -- : --"
                        disabledKeyboardNavigation
                      />
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </FormCard>

            {renderSubmitButtons()}
          </div>
          <div className="col-12 col-xl-6 mb-4">
            {isEmail ? (
              <div className="d-flex align-items-center border p-3">
                <div className="modal-icon mr-4">
                  <AlertCircleOutlineIcon size="38" />
                </div>
                <div className="d-flex flex-column">
                  <p>
                    <strong>
                      {g('emailRecipientsCount', {
                        count: emailRecipientCount ?? '–',
                      })}
                    </strong>
                  </p>
                  <p>{g('emailRecipientsInfo')}</p>
                </div>
              </div>
            ) : (
              <>
                <FormCard title={t('NewsForm.appPreview')} className="mb-3">
                  <PreviewMessages
                    formValues={{
                      heading: formValues?.title,
                      message: formValues?.message,
                      media: formValues?.type,
                    }}
                  />
                </FormCard>
                <div className="d-flex align-items-center border p-3">
                  <div className="modal-icon mr-4">
                    <AlertCircleOutlineIcon size="38" />
                  </div>
                  <div className="d-flex flex-column">
                    <p>{p('pushMessageGroups')}</p>
                    <p>{m('AppStorePushGuidelines')}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </Row>
      </Container>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'goal_sponsor_contact_form',
  validate: validateGoalSponsorContact,
  // Was set by the radios' defaultChecked on mount
  initialValues: { type: 'push', timeToSend: 'INSTANT' },
});
const mapStateToProps = (state) => ({
  formValues: getFormValues('goal_sponsor_contact_form')(state),
});
export default connect(mapStateToProps)(reduxF(GoalSponsorContactForm));
